import React from 'react';

import {Box, Flex, LabelledImage, LabelledImageProps} from 'src/components/shared';
import {Typography} from 'src/components/shared/typography';
import {ChevronRight as ChevronIcon} from 'src/svgs/chevronRight';
import styled from 'styled-components/macro';

const ChangeActiveImageButton = styled.button<{side: 'LEFT' | 'RIGHT'; disabled?: boolean}>`
  all: unset;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 3rem;
  padding: 1rem 1.5rem;
  ${({side}) => {
    return side === 'LEFT'
      ? `
      > svg {
        margin-bottom: -2px;
        margin-left: -2px;
      }
    `
      : `
        > svg {
          transform: rotate(180deg);
          margin-top: 2px;
          margin-right: -2px;
        }
      `;
  }};

  ${({disabled}) => {
    return `
      opacity: ${disabled ? 0 : 1};
      cursor: ${disabled ? 'default' : 'pointer'};
    `;
  }}
`;

const ButtonContainer = styled(Flex).attrs({
  position: 'absolute',
  zIndex: 1,
  width: '100%',
  height: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  pl: 'M',
  pr: 'M',
})``;

const Circle = styled('button')<{active?: boolean; onClick?: () => void}>`
  all: unset;
  cursor: pointer;
  width: 8px;
  height: 8px;
  border-radius: 16px;
  border: solid 1px ${({theme}) => theme.colors.darkGold};
  margin-right: 4px;
  margin-left: 4px;
  background-color: ${({theme, active}) => (active ? theme.colors.darkGold : 'transparent')};
`;

interface EditorialGalleryProps {
  gallery?: Array<LabelledImageProps>;
}

export const EditorialGallery: React.FC<EditorialGalleryProps> = (props: EditorialGalleryProps) => {
  const {activeIndex, setActiveIndex, clickNextSlide, clickPreviousSlide, hasPrevious, hasNext} = useGallerySlider(
    props.gallery?.length || 0,
  );

  return (
    <>
      {typeof props.gallery !== 'undefined' && <Typography.P0 mb="M">Gallery</Typography.P0>}
      {!!props.gallery && (
        <Box position="relative">
          <ButtonContainer>
            <div id="wrapper-to-prevent-flex-collapse">
              <ChangeActiveImageButton side="LEFT" onClick={clickPreviousSlide} disabled={!hasPrevious}>
                <ChevronIcon color="white" />
              </ChangeActiveImageButton>
            </div>
            <ChangeActiveImageButton side="RIGHT" onClick={clickNextSlide} disabled={!hasNext}>
              <ChevronIcon color="white" />
            </ChangeActiveImageButton>
          </ButtonContainer>
          <LabelledImage {...props.gallery[activeIndex]} />
        </Box>
      )}
      <Flex justifyContent="center">
        {props.gallery?.map((v, i) => (
          <Circle key={v._key || i} active={i == activeIndex} onClick={() => setActiveIndex(i)} />
        ))}
      </Flex>
    </>
  );
};

function useGallerySlider(galleryLength: number) {
  const [activeIndex, setActiveIndex] = React.useState(0);

  const clickNextSlide = () => (activeIndex === galleryLength - 1 ? null : setActiveIndex(activeIndex + 1));
  const clickPreviousSlide = () => (activeIndex === 0 ? null : setActiveIndex(activeIndex - 1));

  const hasNext = activeIndex < galleryLength - 1;
  const hasPrevious = activeIndex > 0;

  return {activeIndex, clickNextSlide, clickPreviousSlide, hasNext, hasPrevious, setActiveIndex};
}
